import { useCallback, useEffect, useRef, useState } from 'react';
import { MoveLeftIcon } from 'lucide-react';
import { Button } from 'common/components';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './Privacy.module.scss';

const NAV_ITEMS = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'information', label: 'Information We Collect' },
    { id: 'how-we-use-your-information', label: 'How We Use Your Information' },
    { id: 'how-we-share-your-information', label: 'How We Share Your Information' },
    { id: 'ai-technology-and-data-use', label: 'AI Technology and Data Use' },
    { id: 'data-retention', label: 'Data Retention' },
    { id: 'your-rights-and-choices', label: 'Your Rights and Choices' },
    { id: 'data-security', label: 'Data Security' },
    { id: 'third-party-links-and-services', label: 'Third-Party Links and Services' },
    { id: 'childrens-privacy', label: "Children's Privacy" },
    { id: 'governing-law', label: 'Governing Law' },
    { id: 'changes-to-this-privacy-policy', label: 'Changes to This Privacy Policy' },
    { id: 'contact-us', label: 'Contact Us' },
] as const;

const SCROLL_OFFSET = 120;

export const Privacy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const updateActiveSection = useCallback(() => {
        const container = contentRef.current;
        if (!container) return;
        const sections = container.querySelectorAll<HTMLElement>('h3[id]');
        let current: string | null = null;
        sections.forEach((el) => {
            const top = el.getBoundingClientRect().top;
            if (top <= SCROLL_OFFSET) current = el.id;
        });
        setActiveSectionId(current);
    }, []);

    useEffect(() => {
        updateActiveSection();
        const handler = () => updateActiveSection();
        window.addEventListener('scroll', handler, { passive: true });
        return () => window.removeEventListener('scroll', handler);
    }, [updateActiveSection]);

    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        setActiveSectionId(id);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const navBackHandler = () => {
        if (searchParams.get('demo-request')) {
            navigate('/demo-request');
        } else {
            navigate('/login');
        }
    };

    return (
        <div>
            <div className={styles.header}>
                <div className={styles.container}>
                    <Button variant='link' size='md' onClick={navBackHandler} icon={MoveLeftIcon}>
                        Back
                    </Button>
                    <h1>SLAI</h1>
                    <h2 className='mt-sm'>Privacy Policy</h2>
                    <p className='text-sm mt-xs'>Last Updated: March 1, 2025</p>
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.container}>
                    <div className='flex items-start gap-xl'>
                        <aside className={styles.sidebar}>
                            <ul>
                                {NAV_ITEMS.map((item) => (
                                    <li key={item.id}>
                                        <a
                                            href={`#${item.id}`}
                                            className={activeSectionId === item.id ? styles.active : undefined}
                                            onClick={(e) => handleNavClick(e, item.id)}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                        <main className={styles.content} ref={contentRef}>
                            <h3 id='introduction'>1. Introduction</h3>
                            <p className='font-semibold'>
                                SLAI, LLC ("SLAI," "we," "us," or "our") is committed to protecting the privacy of
                                individuals who interact with our website ("Site"). This Privacy Policy explains what
                                personal information we collect, how we use and protect it, with whom we share it, and
                                your rights regarding that information.
                            </p>
                            <p className='font-semibold'>
                                By submitting your information through any form on this Site, you acknowledge that you
                                have read and understood this Privacy Policy. This Policy is incorporated into and
                                subject to SLAI's Terms of Service.
                            </p>
                            <h3 id='information'>2. Information We Collect</h3>
                            <p className='font-semibold'>
                                We Collect We collect only the personal information you voluntarily provide through our
                                contact and lead capture forms, which includes:{' '}
                            </p>
                            <ul>
                                <li>Full name</li>
                                <li>Phone number</li>
                                <li>
                                    Consent records, including the date, time, and nature of any consent you provide to
                                    receive SMS messages or AI voice agent calls
                                </li>
                            </ul>
                            <p>
                                We do not collect sensitive personal data such as financial information, government
                                identification numbers, health data, credit card information, or similar sensitive
                                categories of information through this Site.
                            </p>
                            <p>
                                We may also automatically collect certain non-personal usage data when you visit our
                                Site, such as browser type, device type, IP address, pages visited, and time spent on
                                the Site. This data is used solely for analytics and Site performance purposes and is
                                not linked to your personal identity.
                            </p>
                            <h3 id='how-we-use-your-information'>3. How We Use Your Information</h3>
                            <p className='font-semibold'>
                                The personal information you provide is used solely for the following purposes:
                            </p>
                            <ul>
                                <li>
                                    To contact you via SMS text message, if you have separately consented to receive
                                    such communications.
                                </li>
                                <li>
                                    To contact you via AI-powered voice agent calls, if you have separately consented to
                                    receive such calls.
                                </li>
                                <li>To respond to your inquiry and provide information about SLAI's services.</li>
                                <li>
                                    To maintain records of communications and consent for legal and compliance purposes,
                                    including compliance with the Telephone Consumer Protection Act (TCPA) and
                                    applicable carrier regulations.
                                </li>
                                <li>
                                    To improve and further develop SLAI's services and technology, using data in
                                    aggregated and de-identified form only.
                                </li>
                            </ul>
                            <p>
                                SLAI will not use your personal information for unsolicited marketing communications
                                beyond the scope of the consent you expressly provide through this Site.
                            </p>
                            <h3 id='how-we-share-your-information'>4. How We Share Your Information</h3>
                            <p className='font-semibold'>
                                SLAI does not sell, rent, or trade your personal information to third parties. We may
                                share your information only in the following limited circumstances:{' '}
                            </p>
                            <ul>
                                <li>
                                    With telephony and communications service providers (such as Twilio) solely as
                                    necessary to deliver SMS messages and AI voice agent calls you have consented to
                                    receive. Such providers are contractually obligated to protect your information and
                                    use it only for the purpose of providing these services.
                                </li>
                                <li>
                                    With our affiliates, subcontractors, and service providers who assist in operating
                                    our platform, all of whom are bound by confidentiality and data protection
                                    obligations no less restrictive than those described in this Policy.
                                </li>
                                <li>
                                    With legal counsel or professional advisors, as necessary to protect SLAI's legal
                                    rights or comply with legal obligations.
                                </li>
                                <li>
                                    With law enforcement or regulatory authorities when required by applicable law,
                                    valid legal process, subpoena, or court order.
                                </li>
                                <li>
                                    Where legally permitted, SLAI will provide you with prior notice of any such
                                    disclosure.
                                </li>
                                <li>
                                    In connection with a merger, acquisition, or sale of all or substantially all of
                                    SLAI's assets, in which case your information may be transferred to the acquiring
                                    entity, subject to the same protections described in this Policy.
                                </li>
                            </ul>
                            <h3 id='ai-technology-and-data-use'>5. AI Technology and Data Use</h3>
                            <p className='font-semibold'>
                                SLAI's platform utilizes artificial intelligence and large language model technology to
                                power its voice agent services. When you interact with or receive a call from an AI
                                voice agent operated by SLAI:
                            </p>
                            <ul>
                                <li>
                                    The call may be recorded and transcribed for quality assurance, compliance, and
                                    service improvement purposes.
                                </li>
                                <li>
                                    Conversation data may be used to improve SLAI's AI models and technology, in
                                    aggregated and de-identified form.
                                </li>
                                <li>AI-generated outputs are provided as-is and may not always be fully accurate.</li>
                                <li>SLAI does not claim ownership over inputs or outputs generated during calls.</li>
                            </ul>
                            <p>
                                SLAI retains all right, title, and interest in its AI technology, models, algorithms,
                                and platform. Your interaction with SLAI's AI services does not grant you any rights in
                                or to SLAI's technology.
                            </p>
                            <h3 id='data-retention'>6. Data Retention</h3>
                            <p className='font-semibold'>
                                We retain your personal information for as long as reasonably necessary to fulfill the
                                purposes described in this Privacy Policy, comply with our legal obligations, maintain
                                consent records as required by applicable law (including TCPA record-keeping
                                requirements), and resolve any disputes.
                            </p>
                            <p className='font-semibold'>
                                When your personal information is no longer needed for these purposes, we will securely
                                delete or anonymize it in accordance with our internal data retention procedures. You
                                may request deletion of your information at any time as described in Section 7 below.
                            </p>
                            <h3 id='your-rights-and-choices'>7. Your Rights and Choices</h3>
                            <p className='font-semibold'>
                                Depending on your state of residence, you may have the following rights with respect to
                                your Depending on your state of residence, you may have the following rights with
                                respect to your personal information:
                            </p>
                            <ul>
                                <li>
                                    Right to Access: You may request a copy of the personal information we hold about
                                    you.
                                </li>
                                <li>
                                    Right to Correction: You may request that we correct inaccurate or incomplete
                                    information.
                                </li>
                                <li>
                                    Right to Deletion: You may request that we delete your personal information, subject
                                    to any legal obligations requiring us to retain certain records.
                                </li>
                                <li>
                                    Right to Opt Out of SMS: You may opt out of SMS communications at any time by
                                    replying STOP to any message you receive from us.
                                </li>
                                <li>
                                    Right to Opt Out of AI Voice Agent Calls: You may opt out of AI voice agent calls by
                                    requesting removal during a call or by contacting us directly at legal@slai.ai.
                                </li>
                                <li>
                                    Right to Non-Discrimination: SLAI will not discriminate against you for exercising
                                    any of your privacy rights. To exercise any of the rights above, please contact us
                                    at legal@slai.ai. We will respond to all verifiable requests within a reasonable
                                    timeframe and in accordance with applicable law. We may require you to verify your
                                    identity before processing your request.
                                </li>
                            </ul>
                            <p>
                                To exercise any of the rights above, please contact us at{' '}
                                <a href='mailto:legal@slai.ai'>legal@slai.ai</a>. We will respond to all verifiable
                                requests within a reasonable timeframe and in accordance with applicable law. We may
                                require you to verify your identity before processing your request.
                            </p>
                            <h3 id='data-security'>8. Data Security</h3>
                            <p className='font-semibold'>
                                SLAI implements commercially reasonable administrative, technical, and physical security
                                measures designed to protect your personal information against unauthorized access,
                                disclosure, alteration, or destruction. Access to personal information is restricted to
                                employees, contractors, and service providers who require it to perform their functions
                                and who are bound by confidentiality obligations. Notwithstanding our security measures,
                                no method of transmission over the internet or electronic storage is completely secure.
                                We cannot guarantee absolute security of your information, and we are not responsible
                                for unauthorized access resulting from circumstances beyond our reasonable control,
                                including failures on the part of third-party systems or your own device security.{' '}
                            </p>
                            <h3 id='third-party-links-and-services'>9. Third-Party Links and Services</h3>
                            <p className='font-semibold'>
                                This Site may contain links to third-party websites or services. SLAI is not responsible
                                for the privacy practices, content, or data handling of any third-party site. We
                                encourage you to review the privacy policies of any third-party sites you visit. The
                                appearance of a link on our Site does not imply endorsement or sponsorship by SLAI.{' '}
                            </p>
                            <h3 id='childrens-privacy'>10. Children's Privacy</h3>
                            <p className='font-semibold'>
                                This Site is not directed to individuals under the age of 18, and SLAI does not
                                knowingly collect personal information from minors. If you believe we have inadvertently
                                collected information from a minor, please contact us immediately at legal@slai.ai and
                                we will promptly delete such information from our records.{' '}
                            </p>
                            <h3 id='governing-law'>11. Governing Law</h3>
                            <p className='font-semibold'>
                                This Privacy Policy is governed by and construed in accordance with the laws of the
                                State of Florida, without giving effect to its conflict of law principles. Any disputes
                                arising under this Policy shall be subject to the exclusive jurisdiction of the state
                                and federal courts located in Miami, Florida.{' '}
                            </p>
                            <h3 id='changes-to-this-privacy-policy'>12. Changes to This Privacy Policy</h3>
                            <p className='font-semibold'>
                                SLAI reserves the right to update or modify this Privacy Policy at any time. Privacy
                                Policy SLAI reserves the right to update or modify this Privacy Policy at any time. When
                                we make changes, we will revise the "Last Updated" date at the top of this page. Your
                                continued use of the Site or submission of information following any changes constitutes
                                your acceptance of the revised Policy. We encourage you to review this page periodically
                                to stay informed of how we protect your information.{' '}
                            </p>
                            <h3 id='contact-us'>13. Contact Us</h3>
                            <p className='font-semibold'>
                                For questions, concerns, or requests related to this Privacy Policy or the handling of
                                your personal information, please contact us at:
                            </p>
                            <p className='mt-lg font-semibold'>
                                SLAI, LLC | <a href='mailto:legal@slai.ai'>legal@slai.ai</a> |{' '}
                                <a href='https://www.slai.ai'>www.slai.ai</a>
                            </p>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Privacy;
